// Single source of truth for the ARG manual-invoice billing-week date rules.
//
// Two deliberately separate predicates:
//   meetsBillingWeekInvariant - the durable data rule. A stored billing week is
//     correct iff it is a Monday on or after the promo end date. Time-invariant,
//     so it stays true for historical promos and is safe to run on every save.
//   isSelectableBillingWeek   - the picker rule. The invariant, plus a floor of
//     three weeks ago so the calendar does not offer a batch that has already
//     run. Time-relative, so it must NOT be used to validate stored values.
//
// Conflating the two is what let this bug through: the picker rule was the only
// enforcement anywhere, and it silently degraded when end_dt was null.

const MONDAY = 1
const SELECTABLE_FLOOR_DAYS = 21

export const BILLING_WEEK_INVALID = 'Billing Week must be a Monday on or after the promo End Date'
export const BILLING_WEEK_NEEDS_END_DT = 'Enter the promo End Date before selecting a Billing Week'

// Parse a 'YYYY-MM-DD' string to local midnight. Returns null for anything else.
//
// The 'T00:00:00' suffix is what makes this local. A date-only string is parsed
// as UTC per spec, so new Date('2026-08-03') is the previous day in any US
// timezone -- and the values it gets compared against are local. Appending a
// time makes it a date-time without an offset, which is parsed as local.
export function parseLocalDate (value) {
  if (typeof value !== 'string') return null
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  // A real-looking but impossible date rolls over silently rather than failing:
  // '2026-02-30' becomes March 2nd, which happens to be a Monday. Rollover
  // always changes the day of the month, so compare it back against the input.
  if (date.getDate() !== Number(value.slice(8, 10))) return null
  return date
}

function startOfLocalDay (date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

// The invariant. Fails closed on any missing or unparseable input.
export function meetsBillingWeekInvariant (weekValue, endDtValue) {
  const week = parseLocalDate(weekValue)
  if (!week || week.getDay() !== MONDAY) return false
  const endDt = parseLocalDate(endDtValue)
  if (!endDt) return false
  return week.getTime() >= endDt.getTime()
}

// The invariant plus the recency floor. Equivalent to the previous
// isAllowedBillingWeekDate (Monday && week >= max(end_dt, today - 21d)) whenever
// end_dt parses; differs only in failing closed when it does not.
export function isSelectableBillingWeek (weekValue, endDtValue, today = new Date()) {
  if (!meetsBillingWeekInvariant(weekValue, endDtValue)) return false
  const floor = startOfLocalDay(today)
  floor.setDate(floor.getDate() - SELECTABLE_FLOOR_DAYS)
  return parseLocalDate(weekValue).getTime() >= floor.getTime()
}
